import { motion, useReducedMotion } from "framer-motion";
import { Sparkles, ArrowUpRight } from "lucide-react";
import { globalReach, globalStats, homeBase } from "@/data/globalReach";

/**
 * NetworkMap — a restrained, on-brand "creative network" visual: connective
 * lines radiate from a central Bangladesh hub to regional nodes. Motion is a
 * single one-time draw-in (pathLength) + a soft hub pulse; fully disabled under
 * reduced-motion (Emil). No layout dependency, no data fetch — decorative only.
 */
const NET_NODES = [
    { x: 150, y: 110, label: "NA", r: 6 },
    { x: 360, y: 84, label: "UK", r: 5 },
    { x: 770, y: 116, label: "EU", r: 6 },
    { x: 700, y: 244, label: "ME", r: 5 },
    { x: 856, y: 300, label: "APAC", r: 6 },
    { x: 604, y: 402, label: "AU", r: 5 },
    { x: 300, y: 344, label: "SA", r: 5 },
    { x: 168, y: 372, label: "AF", r: 6 },
];
const HUB = { x: 480, y: 236 };

const NetworkMap = () => {
    const reduce = useReducedMotion();
    return (
        <div className="relative w-full" data-testid="global-network-map" aria-hidden>
            <svg
                viewBox="0 0 960 480"
                className="h-auto w-full"
                style={{ maxHeight: "clamp(240px, 40vh, 420px)" }}
                fill="none"
            >
                {/* connective spokes */}
                {NET_NODES.map((n, i) => (
                    <motion.line
                        key={`l-${n.label}`}
                        x1={HUB.x}
                        y1={HUB.y}
                        x2={n.x}
                        y2={n.y}
                        stroke="var(--pg-accent)"
                        strokeWidth="1"
                        strokeOpacity="0.34"
                        initial={reduce ? { pathLength: 1, opacity: 0.34 } : { pathLength: 0, opacity: 0 }}
                        whileInView={{ pathLength: 1, opacity: 0.34 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: reduce ? 0 : 1.1, delay: reduce ? 0 : 0.15 + i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                    />
                ))}

                {/* regional nodes */}
                {NET_NODES.map((n, i) => (
                    <motion.g
                        key={`n-${n.label}`}
                        initial={reduce ? { opacity: 1 } : { opacity: 0, scale: 0.4 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: reduce ? 0 : 0.5, delay: reduce ? 0 : 0.55 + i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                        style={{ transformOrigin: `${n.x}px ${n.y}px` }}
                    >
                        <circle cx={n.x} cy={n.y} r={n.r + 5} fill="var(--pg-accent)" fillOpacity="0.10" />
                        <circle cx={n.x} cy={n.y} r={n.r} fill="var(--pg-accent)" fillOpacity="0.85" />
                        <text
                            x={n.x}
                            y={n.y - n.r - 8}
                            textAnchor="middle"
                            style={{ fontFamily: "var(--font-mono)", fontSize: "12px", letterSpacing: "0.16em", fill: "var(--pg-fg-muted)" }}
                        >
                            {n.label}
                        </text>
                    </motion.g>
                ))}

                {/* Bangladesh hub */}
                <motion.circle
                    cx={HUB.x}
                    cy={HUB.y}
                    r="30"
                    fill="var(--pg-accent)"
                    fillOpacity="0.12"
                    initial={reduce ? { opacity: 0.12 } : { opacity: 0.05, scale: 0.9 }}
                    animate={reduce ? undefined : { opacity: [0.1, 0.22, 0.1], scale: [1, 1.12, 1] }}
                    transition={reduce ? undefined : { duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
                    style={{ transformOrigin: `${HUB.x}px ${HUB.y}px` }}
                />
                <circle cx={HUB.x} cy={HUB.y} r="15" fill="var(--pg-accent)" />
                <text
                    x={HUB.x}
                    y={HUB.y + 44}
                    textAnchor="middle"
                    style={{ fontFamily: "var(--font-mono)", fontSize: "12px", letterSpacing: "0.2em", fill: "var(--pg-fg)" }}
                >
                    {homeBase.iso} · BANGLADESH
                </text>
            </svg>
        </div>
    );
};

/**
 * GlobalPresence — luxury minimal "Creative Network" visualization with
 * refined hover sophistication, motion polish, and depth restraint.
 */
export const GlobalPresence = () => {
    return (
        <section
            id="global"
            className="pg-section relative"
            aria-label="Global Creative Network"
            data-testid="global-presence-section"
            style={{ background: "var(--pg-bg)" }}
        >
            {/* Atmospheric backdrop */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0"
                style={{
                    background:
                        "radial-gradient(900px 500px at 80% 20%, rgba(47,227,208,0.08) 0%, rgba(47,227,208,0) 60%), radial-gradient(900px 500px at 10% 80%, rgba(var(--pg-accent-rgb),0.08) 0%, rgba(var(--pg-accent-rgb),0) 55%)",
                }}
            />

            {/* Decorative blueprint dot pattern with subtle drift */}
            <motion.div
                aria-hidden
                className="pointer-events-none absolute inset-0 opacity-40"
                style={{
                    backgroundImage:
                        "radial-gradient(rgba(120,220,210,0.18) 1px, transparent 1px)",
                    backgroundSize: "22px 22px",
                    maskImage:
                        "radial-gradient(ellipse 70% 60% at 50% 40%, black 30%, transparent 80%)",
                    WebkitMaskImage:
                        "radial-gradient(ellipse 70% 60% at 50% 40%, black 30%, transparent 80%)",
                }}
                animate={{ backgroundPosition: ["0px 0px", "22px 22px"] }}
                transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
            />

            <div className="pg-container relative">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                    className="max-w-3xl"
                >
                    <h2
                        className="pg-h2-section"
                        style={{ color: "var(--pg-fg)" }}
                    >
                        A studio with a <span className="pg-text-gold-static">global footprint</span>.
                    </h2>
                    <p className="pg-lede mt-5 max-w-xl">
                        From Pranto&rsquo;s studio base in <span style={{ color: "var(--pg-fg)" }}>{homeBase.name.split(" ·")[0]}</span>,
                        projects flow to clients on every continent — across <span style={{ color: "var(--pg-fg)" }}>{globalStats.totalCountries}+ countries</span> and counting.
                    </p>
                </motion.div>

                {/* Creative network visual */}
                <div className="mt-10 sm:mt-12">
                    <NetworkMap />
                </div>

                {/* Stats row */}
                <div className="mt-12 grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <StatTile label="Studio Base" value={homeBase.iso} />
                    <StatTile label="Countries Reached" value={globalStats.totalCountries} />
                    <StatTile label="High-Value Brands" value={globalStats.highValueBrands} />
                    <StatTile label="Revenue Impact" value={globalStats.revenueImpact} />
                </div>

                {/* Country constellation grid */}
                <div className="mt-10">
                    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
                        <div>
                            <span
                                className="font-mono text-[11px] uppercase tracking-[0.22em]"
                                style={{ color: "var(--pg-fg-muted)" }}
                            >
                                Live Touchpoints · Active Engagements
                            </span>
                            <p className="mt-1.5 max-w-md text-sm" style={{ color: "var(--pg-fg-muted)" }}>
                                Each card represents an active region the studio currently serves.
                            </p>
                        </div>
                        <span className="pg-chip inline-flex items-center gap-2">
                            <motion.span
                                aria-hidden
                                className="inline-block h-1.5 w-1.5 rounded-full"
                                style={{ background: "var(--pg-accent)" }}
                                animate={{ opacity: [0.4, 1, 0.4] }}
                                transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
                            />
                            Network Active
                        </span>
                    </div>

                    <div
                        className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4"
                        data-testid="global-presence-list"
                    >
                        {/* Home base card */}
                        <HomeBaseCard />

                        {/* Reached countries */}
                        {globalReach.map((c, idx) => (
                            <CountryCard key={c.iso} country={c} index={idx} />
                        ))}
                    </div>
                </div>

                {/* Footer signature */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="mt-12 flex flex-wrap items-center justify-between gap-3 rounded-2xl border p-5 sm:p-6"
                    style={{ borderColor: "var(--pg-border)", background: "var(--pg-surface)" }}
                >
                    <div>
                        <span
                            className="font-mono text-[11px] uppercase tracking-[0.22em]"
                            style={{ color: "var(--pg-fg-muted)" }}
                        >
                            Working Globally · From Bangladesh, To The World
                        </span>
                        <p className="mt-2 text-base sm:text-lg" style={{ color: "var(--pg-fg)" }}>
                            <span className="pg-text-gold-static font-display">{globalStats.highValueBrands}</span> high-value brands ·{" "}
                            <span className="pg-text-gold-static font-display">{globalStats.additionalProjects}</span> additional projects ·{" "}
                            <span className="pg-text-gold-static font-display">{globalStats.revenueImpact}</span> client revenue.
                        </p>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

const StatTile = ({ label, value }) => (
    <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.5 }}
        whileHover={{ y: -4 }}
        className="relative overflow-hidden rounded-2xl border p-5 text-center transition-colors duration-300"
        style={{ borderColor: "var(--pg-border)", background: "var(--pg-surface)" }}
    >
        <span
            aria-hidden
            className="absolute inset-x-0 top-0 h-px"
            style={{
                background:
                    "linear-gradient(90deg, transparent, color-mix(in srgb, var(--pg-accent) 55%, transparent), transparent)",
            }}
        />
        <div className="font-display text-2xl sm:text-3xl pg-text-gold-static">{value}</div>
        <div
            className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.18em]"
            style={{ color: "var(--pg-fg-muted)" }}
        >
            {label}
        </div>
    </motion.div>
);

const HomeBaseCard = () => (
    <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
        whileHover={{ y: -6 }}
        className="group relative overflow-hidden rounded-2xl border p-5 transition-[transform,border-color] duration-300"
        style={{
            borderColor: "rgba(var(--pg-accent-rgb), 0.55)",
            background:
                "linear-gradient(180deg, color-mix(in srgb, var(--pg-accent) 12%, var(--pg-surface)) 0%, var(--pg-surface) 100%)",
            boxShadow: "0 14px 36px -16px color-mix(in srgb, var(--pg-accent) 35%, transparent)",
        }}
        data-testid="global-country-bd"
    >
        {/* Animated ambient glow on hover */}
        <span
            aria-hidden
            className="pointer-events-none absolute -inset-px rounded-2xl opacity-0 transition-opacity duration-500 group-hover:opacity-100"
            style={{
                background:
                    "radial-gradient(circle at 30% 0%, color-mix(in srgb, var(--pg-accent) 30%, transparent), transparent 60%)",
            }}
        />
        <div className="relative flex items-center justify-between">
            <span
                className="inline-flex h-9 w-9 items-center justify-center rounded-full font-mono text-[11px] uppercase tracking-[0.16em]"
                style={{ background: "var(--pg-accent)", color: "#071112" }}
            >
                {homeBase.iso}
            </span>
            <motion.span
                aria-hidden
                animate={{ opacity: [0.6, 1, 0.6] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            >
                <Sparkles className="h-3.5 w-3.5" style={{ color: "var(--pg-accent)" }} />
            </motion.span>
        </div>
        <div className="relative mt-4">
            <div className="font-display text-lg leading-tight" style={{ color: "var(--pg-fg)" }}>
                Bangladesh
            </div>
            <div
                className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em]"
                style={{ color: "var(--pg-accent)" }}
            >
                Studio Base · Headquarters
            </div>
        </div>
    </motion.div>
);

const CountryCard = ({ country, index }) => (
    <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.5, delay: 0.04 * index, ease: [0.16, 1, 0.3, 1] }}
        whileHover={{ y: -6 }}
        className="group relative overflow-hidden rounded-2xl border p-5 transition-[transform,border-color] duration-300"
        style={{ borderColor: "var(--pg-border)", background: "var(--pg-surface)" }}
        data-testid={`global-country-${country.iso.toLowerCase()}`}
    >
        {/* Hover ambient glow */}
        <span
            aria-hidden
            className="pointer-events-none absolute -inset-px rounded-2xl opacity-0 transition-opacity duration-500 group-hover:opacity-100"
            style={{
                background:
                    "radial-gradient(circle at 50% 0%, color-mix(in srgb, var(--pg-accent) 20%, transparent), transparent 65%)",
            }}
        />
        {/* Hover accent top line */}
        <span
            aria-hidden
            className="absolute inset-x-0 top-0 h-px opacity-0 transition-opacity duration-500 group-hover:opacity-100"
            style={{
                background:
                    "linear-gradient(90deg, transparent, var(--pg-accent), transparent)",
            }}
        />

        <div className="relative flex items-center justify-between">
            <span
                className="inline-flex h-9 w-9 items-center justify-center rounded-full font-mono text-[11px] uppercase tracking-[0.16em] transition-colors duration-300 group-hover:bg-[color-mix(in_srgb,var(--pg-accent)_18%,var(--pg-surface-2))]"
                style={{ background: "var(--pg-surface-2)", color: "var(--pg-fg)" }}
            >
                {country.iso}
            </span>
            <ArrowUpRight
                className="h-3.5 w-3.5 opacity-0 transition-[opacity,transform] duration-300 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                style={{ color: "var(--pg-accent)" }}
                strokeWidth={1.6}
            />
        </div>
        <div className="relative mt-4">
            <div className="font-display text-lg leading-tight" style={{ color: "var(--pg-fg)" }}>
                {country.name}
            </div>
            <div
                className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em]"
                style={{ color: "var(--pg-fg-muted)" }}
            >
                <span className="pg-text-gold-static font-display text-sm">{country.projects}</span> projects
            </div>
        </div>
    </motion.div>
);

export default GlobalPresence;
